import { Direction, BidiModule } from '@angular/cdk/bidi';
import {
  AfterViewInit,
  Component,
  DestroyRef,
  HostListener,
  Inject,
  NgZone,
  OnDestroy,
  OnInit,
  Renderer2
} from '@angular/core';
import { DirectionService, Config, RightSidebarService } from '@config';
import { ConfigService } from '@config/config.service';
import { DOCUMENT } from '@angular/common';
import {Router, RouterOutlet} from '@angular/router';
import { RightSidebarComponent } from '../../right-sidebar/right-sidebar.component';
import { SidebarComponent } from '../../sidebar/sidebar.component';
import { HeaderComponent } from '../../header/header.component';
import {fetchAuthSession, signOut} from "@aws-amplify/auth";
import {EMPTY, Observable, retry, tap} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {catchError} from "rxjs/operators";
import {WebsocketService} from "@shared/services/websocket.service";
import {MatSnackBar} from "@angular/material/snack-bar";
import {WebSocketMessage} from "@shared/types/webSocketMessage";
import {BadgeService} from "@shared/services/badge.service";
import {AuthService} from "../../../auth/auth.service";

@Component({
  selector: 'app-main-layout',
  templateUrl: './main-layout.component.html',
  styleUrls: [],
  standalone: true,
  imports: [
    HeaderComponent,
    SidebarComponent,
    RightSidebarComponent,
    BidiModule,
    RouterOutlet
  ],
  providers: [RightSidebarService]
})
export class MainLayoutComponent implements AfterViewInit, OnInit, OnDestroy {
  direction!: Direction;
  public config!: Config;
  userActivity: any;
  interval: any;
  inactivitySeconds = 0;
  messages: WebSocketMessage[] = [];
  pendingCount!: Observable<number>;
  private readonly sideClosed = 'side-closed';
  private readonly submenuClosed = 'submenu-closed';
  constructor(
    private readonly directionService: DirectionService,
    private readonly configService: ConfigService,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly renderer: Renderer2,
    private readonly zone: NgZone,
    private readonly router: Router,
    private readonly webSocketService: WebsocketService,
    private readonly destroyRef: DestroyRef,
    private readonly snackBar: MatSnackBar,
    private readonly badgeService: BadgeService,
    private readonly authService: AuthService
  ) {
    this.config = this.configService.configData;
    this.directionService.currentData.subscribe((currentData) => {
      if (currentData) {
        this.direction = currentData === 'ltr' ? 'ltr' : 'rtl';
      } else {
        if (localStorage.getItem('isRtl')) {
          if (localStorage.getItem('isRtl') === 'true') {
            this.direction = 'rtl';
          }
          if (localStorage.getItem('isRtl') === 'false') {
            this.direction = 'ltr';
          }
        } else {
          if (this.config) {
            if (this.config.layout.rtl) {
              this.direction = 'rtl';
              localStorage.setItem('isRtl', 'true');
            } else {
              this.direction = 'ltr';
              localStorage.setItem('isRtl', 'false');
            }
          }
        }
      }
    });

    this.zone.runOutsideAngular(() => {
      this.setTimeout();
      this.runTimer();
    });
  }

  async ngOnInit(){
    const socket = await this.webSocketService.connect();
    socket.pipe(
      retry({count: 3, delay: 1000}),
      takeUntilDestroyed(this.destroyRef),
      tap({
        error: (err) => {console.error(err)}
      }), catchError(_ => {return EMPTY})
    ).subscribe((value: WebSocketMessage) => {
      this.messages.push(value);
      this.pendingCount = this.badgeService.getCount();
      const message = `${value.payload.name} has requested access. Please review and approve.`;
      this.snackBar.open(message, 'Take me there', {
        panelClass: 'snackbar-info',
        duration: 10000
      }).onAction().subscribe(action => {
        this.router.navigate(['/admin']);
      });
    });
    this.pendingCount = this.badgeService.getCount().pipe(takeUntilDestroyed(this.destroyRef));
  }

  ngOnDestroy(){
    this.webSocketService.close();
  }

  async setTimeout(): Promise<void> {
    this.userActivity = setTimeout(() => {
      signOut().then(() => {
        this.inactivitySeconds = 0;
        this.zone.run(async () => {
          await this.router.navigate(['/auth/sign-in']);
        });
      })
      .catch((error) => {
        console.error('Error during sign-out:', error);
      });
    }, 60 * 60000);
  }

  runTimer(): void {
    this.interval = setInterval(() => {
      this.inactivitySeconds++;
    }, 1000);
  }

  @HostListener('mouseup')
  @HostListener('window:scroll')
  @HostListener('keyup')
  async refreshUserState(): Promise<void> {
    clearTimeout(this.userActivity);

    await this.authService.checkSession();

    if (this.inactivitySeconds > 60) {
      this.inactivitySeconds = 0;
      await fetchAuthSession({forceRefresh: true});
    }
    await this.setTimeout();
  }

  ngAfterViewInit(): void {
    //------------ set varient start----------------
    if (localStorage.getItem('theme')) {
      this.renderer.removeClass(this.document.body, this.config.layout.variant);
      this.renderer.addClass(
        this.document.body,
        localStorage.getItem('theme') as string
      );
    } else {
      this.renderer.addClass(this.document.body, this.config.layout.variant);
      localStorage.setItem('theme', this.config.layout.variant);
    }

    //------------ set varient end----------------

    //------------ set theme start----------------

    if (localStorage.getItem('choose_skin')) {
      this.renderer.removeClass(
        this.document.body,
        'theme-' + this.config.layout.theme_color
      );

      this.renderer.addClass(
        this.document.body,
        localStorage.getItem('choose_skin') as string
      );
      localStorage.setItem(
        'choose_skin_active',
        (localStorage.getItem('choose_skin') as string).substring(6)
      );
    } else {
      this.renderer.addClass(
        this.document.body,
        'theme-' + this.config.layout.theme_color
      );

      localStorage.setItem(
        'choose_skin',
        'theme-' + this.config.layout.theme_color
      );
      localStorage.setItem(
        'choose_skin_active',
        this.config.layout.theme_color
      );
    }

    //------------ set theme end----------------

    //------------ set RTL start----------------

    if (localStorage.getItem('isRtl')) {
      if (localStorage.getItem('isRtl') === 'true') {
        this.setRTLSettings();
      }
      if (localStorage.getItem('isRtl') === 'false') {
        this.setLTRSettings();
      }
    } else {
      if (this.config.layout.rtl) {
        this.setRTLSettings();
      } else {
        this.setLTRSettings();
      }
    }
    //------------ set RTL end----------------

    //------------ set sidebar color start----------------

    if (localStorage.getItem('menuOption')) {
      this.renderer.addClass(
        this.document.body,
        localStorage.getItem('menuOption') as string
      );
    } else {
      this.renderer.addClass(
        this.document.body,
        'menu_' + this.config.layout.sidebar.backgroundColor
      );
      localStorage.setItem(
        'menuOption',
        'menu_' + this.config.layout.sidebar.backgroundColor
      );
    }

    //------------ set sidebar color end----------------

    //------------ set logo color start----------------

    if (localStorage.getItem('choose_logoheader')) {
      this.renderer.addClass(
        this.document.body,
        localStorage.getItem('choose_logoheader') as string
      );
    } else {
      this.renderer.addClass(
        this.document.body,
        'logo-' + this.config.layout.logo_bg_color
      );
    }

    //------------ set logo color end----------------

    //------------ set sidebar collapse start----------------
    if (localStorage.getItem('collapsed_menu')) {
      if (localStorage.getItem('collapsed_menu') === 'true') {
        this.renderer.addClass(this.document.body, this.sideClosed);
        this.renderer.addClass(this.document.body, this.submenuClosed);
      }
    } else {
      if (this.config.layout.sidebar.collapsed) {
        this.renderer.addClass(this.document.body, this.sideClosed);
        this.renderer.addClass(this.document.body, this.submenuClosed);
        localStorage.setItem('collapsed_menu', 'true');
      } else {
        this.renderer.removeClass(this.document.body, this.sideClosed);
        this.renderer.removeClass(this.document.body, this.submenuClosed);
        localStorage.setItem('collapsed_menu', 'false');
      }
    }

    //------------ set sidebar collapse end----------------
  }

  setRTLSettings() {
    document.getElementsByTagName('html')[0].setAttribute('dir', 'rtl');
    this.renderer.addClass(this.document.body, 'rtl');

    localStorage.setItem('isRtl', 'true');
  }
  setLTRSettings() {
    document.getElementsByTagName('html')[0].removeAttribute('dir');
    this.renderer.removeClass(this.document.body, 'rtl');

    localStorage.setItem('isRtl', 'false');
  }
}
